// Phase SIKA-3 — Edit Goal Modal.
//
// Source: SIKA_Independence_Architecture_v1.0 §02.1 (mode toggle) +
// §06 trigger #2 / #3 (goal target / deadline change recalibration).
//
// Composes Pill primitive from super-acca; modal shell pattern mirrors
// SetSeedStakeModal (SC-G1). No primitive edits, no token mutations.
//
// Backend: sika_update_goal(p_goal_id uuid, p_payload jsonb) RPC.
// Server-side AFTER UPDATE trigger on sika_goals fires sika_recalibrate
// when target_amount or deadline changes (SIKA-2 plumbing).

import { useCallback, useEffect, useState } from "react";
import { Pill } from "../super-acca";
import { getBrowserClient } from "../../supabase/browser.ts";

export interface EditGoalModalGoal {
  id: string;
  title: string;
  target_amount: number;
  deadline: string | null;
  mode: "simple" | "chain";
  linked_chain_id: string | null;
}

export interface EditGoalModalChain {
  id: string;
  current_value: number;
  status: string;
}

export interface EditGoalModalProps {
  open: boolean;
  goal: EditGoalModalGoal | null;
  /** User's active rollover chains, for the linked-chain selector. */
  chains?: EditGoalModalChain[];
  onClose: () => void;
  onSaved?: () => void;
  /** Test seam — override the RPC call. */
  updateGoal?: (
    goalId: string,
    payload: Record<string, unknown>,
  ) => Promise<{ ok: boolean; reason?: string }>;
}

async function defaultUpdateGoal(
  goalId: string,
  payload: Record<string, unknown>,
): Promise<{ ok: boolean; reason?: string }> {
  const client = getBrowserClient();
  const { data, error } = await (
    client.rpc as unknown as (
      fn: string,
      args: Record<string, unknown>,
    ) => Promise<{ data: { ok?: boolean; reason?: string } | null; error: { message: string } | null }>
  )("sika_update_goal", { p_goal_id: goalId, p_payload: payload });
  if (error) return { ok: false, reason: error.message };
  return { ok: data?.ok ?? false, reason: data?.reason };
}

const OVERLAY_STYLE: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0, 0, 0, 0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9000,
};

const MODAL_STYLE: React.CSSProperties = {
  width: "min(440px, 92vw)",
  background: "var(--sa-bg-elevated, #14171c)",
  border: "1px solid var(--sa-hairline, #2a2f38)",
  borderRadius: 6,
  padding: "22px 22px 18px",
  color: "var(--sa-fg-primary, #e8ebf0)",
  fontFamily: "var(--sa-font-body)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
};

const INPUT_STYLE: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 3,
  border: "1px solid var(--sa-hairline, #2a2f38)",
  background: "var(--sa-bg-base, #0e1014)",
  color: "var(--sa-fg-primary, #e8ebf0)",
  fontFamily: "var(--sa-font-mono)",
  fontSize: 14,
  letterSpacing: "0.04em",
};

const LABEL_STYLE: React.CSSProperties = {
  display: "block",
  fontSize: 11,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "var(--sa-fg-secondary, #a8b0bd)",
  margin: "12px 0 6px",
};

const CTA_PRIMARY: React.CSSProperties = {
  marginTop: 14,
  width: "100%",
  padding: "11px 14px",
  borderRadius: 3,
  border: "1px solid var(--sa-accent-saffron)",
  background: "rgba(255, 200, 64, 0.18)",
  color: "var(--sa-accent-saffron)",
  fontFamily: "var(--sa-font-mono)",
  fontSize: 11,
  letterSpacing: "0.16em",
  textTransform: "uppercase" as const,
  cursor: "pointer",
};

export function EditGoalModal({
  open,
  goal,
  chains = [],
  onClose,
  onSaved,
  updateGoal = defaultUpdateGoal,
}: EditGoalModalProps): JSX.Element | null {
  // Initial state is seeded from goal so SSR + first paint show the
  // correct fields (no flash, and tests can SSR-render meaningfully).
  const [title, setTitle] = useState<string>(() => goal?.title ?? "");
  const [target, setTarget] = useState<string>(
    () => (goal ? String(goal.target_amount) : ""),
  );
  const [deadline, setDeadline] = useState<string>(
    () => (goal?.deadline ? goal.deadline.slice(0, 10) : ""),
  );
  const [mode, setMode] = useState<"simple" | "chain">(() => goal?.mode ?? "simple");
  const [linkedChainId, setLinkedChainId] = useState<string>(
    () => goal?.linked_chain_id ?? "",
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-sync when a different goal is supplied (modal reused across goals).
  useEffect(() => {
    if (!goal) return;
    setTitle(goal.title);
    setTarget(String(goal.target_amount));
    setDeadline(goal.deadline ? goal.deadline.slice(0, 10) : "");
    setMode(goal.mode);
    setLinkedChainId(goal.linked_chain_id ?? "");
    setError(null);
  }, [goal]);

  const submit = useCallback(async () => {
    if (!goal) return;
    setError(null);
    const targetNum = Number(target);
    if (!Number.isFinite(targetNum) || targetNum <= 0) {
      setError("Enter a positive target amount.");
      return;
    }
    if (mode === "chain" && linkedChainId.length === 0) {
      setError("Select a ROLLOVER chain to link this goal to Chain Mode.");
      return;
    }
    const payload: Record<string, unknown> = {
      title: title.trim(),
      target_amount: targetNum,
      mode,
      // Always include the linked_chain_id key so the RPC honors mode flips.
      linked_chain_id: mode === "chain" ? linkedChainId : null,
    };
    if (deadline.length > 0) {
      payload.deadline = deadline;
    }
    setSubmitting(true);
    const res = await updateGoal(goal.id, payload);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.reason ?? "Could not save changes.");
      return;
    }
    onSaved?.();
    onClose();
  }, [goal, title, target, deadline, mode, linkedChainId, updateGoal, onSaved, onClose]);

  if (!open || !goal) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="sika-edit-goal-title"
      style={OVERLAY_STYLE}
      data-testid="sika-edit-modal"
    >
      <div style={MODAL_STYLE}>
        <h2
          id="sika-edit-goal-title"
          style={{
            margin: 0,
            fontFamily: "var(--sa-font-display)",
            fontSize: 18,
            letterSpacing: "0.04em",
          }}
        >
          Edit goal
        </h2>
        <div
          style={{
            fontSize: 12,
            color: "var(--sa-fg-secondary, #a8b0bd)",
            margin: "8px 0 0",
          }}
        >
          Changing target or deadline triggers a transparent recalibration.
        </div>

        <label style={LABEL_STYLE}>Goal name</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={INPUT_STYLE}
          data-testid="sika-edit-title"
        />

        <label style={LABEL_STYLE}>Target amount (GH₵)</label>
        <input
          type="number"
          inputMode="decimal"
          min={1}
          step={1}
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          style={INPUT_STYLE}
          data-testid="sika-edit-target"
        />

        <label style={LABEL_STYLE}>Target date</label>
        <input
          type="date"
          value={deadline}
          onChange={(e) => setDeadline(e.target.value)}
          style={INPUT_STYLE}
          data-testid="sika-edit-deadline"
        />

        <label style={LABEL_STYLE}>Mode</label>
        <div style={{ display: "flex", gap: 8 }} data-testid="sika-edit-mode">
          <button
            type="button"
            onClick={() => setMode("simple")}
            data-active={mode === "simple"}
            style={{
              flex: 1,
              padding: "8px 10px",
              borderRadius: 3,
              border: `1px solid ${mode === "simple" ? "var(--sa-accent-saffron)" : "var(--sa-hairline)"}`,
              background: mode === "simple" ? "rgba(255,200,64,0.12)" : "transparent",
              color: mode === "simple" ? "var(--sa-accent-saffron)" : "var(--sa-fg-secondary)",
              fontFamily: "var(--sa-font-mono)",
              fontSize: 11,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              cursor: "pointer",
            }}
          >
            Simple
          </button>
          <button
            type="button"
            onClick={() => setMode("chain")}
            data-active={mode === "chain"}
            style={{
              flex: 1,
              padding: "8px 10px",
              borderRadius: 3,
              border: `1px solid ${mode === "chain" ? "var(--sa-accent-saffron)" : "var(--sa-hairline)"}`,
              background: mode === "chain" ? "rgba(255,200,64,0.12)" : "transparent",
              color: mode === "chain" ? "var(--sa-accent-saffron)" : "var(--sa-fg-secondary)",
              fontFamily: "var(--sa-font-mono)",
              fontSize: 11,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              cursor: "pointer",
            }}
          >
            Chain
          </button>
        </div>

        {mode === "chain" ? (
          <>
            <label style={LABEL_STYLE}>Linked ROLLOVER chain</label>
            {chains.length === 0 ? (
              <div style={{ marginTop: 4 }}>
                <Pill
                  color="var(--sa-fg-secondary)"
                  borderColor="var(--sa-hairline)"
                  background="transparent"
                  style={{ whiteSpace: "normal", lineHeight: 1.4, padding: "6px 10px" }}
                >
                  No active chains. Start a ROLLOVER chain to enable Chain Mode.
                </Pill>
              </div>
            ) : (
              <select
                value={linkedChainId}
                onChange={(e) => setLinkedChainId(e.target.value)}
                style={INPUT_STYLE}
                data-testid="sika-edit-chain"
              >
                <option value="">— Select chain —</option>
                {chains.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.id.slice(0, 8)} · GH₵{c.current_value.toLocaleString()} · {c.status}
                  </option>
                ))}
              </select>
            )}
          </>
        ) : null}

        {error && (
          <div
            role="alert"
            style={{
              marginTop: 10,
              fontSize: 12,
              color: "var(--sa-signal-red, #ff6266)",
            }}
            data-testid="sika-edit-error"
          >
            {error}
          </div>
        )}

        <button
          type="button"
          style={CTA_PRIMARY}
          onClick={() => void submit()}
          disabled={submitting}
          data-testid="sika-edit-submit"
        >
          {submitting ? "Saving…" : "Save changes"}
        </button>

        <button
          type="button"
          onClick={onClose}
          style={{
            ...CTA_PRIMARY,
            marginTop: 8,
            border: "1px solid var(--sa-hairline)",
            background: "transparent",
            color: "var(--sa-fg-secondary)",
          }}
          data-testid="sika-edit-cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
